import { useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Adjunto, type Aporte, type AporteGrupo, type Caja, type FiduciantePublico, type Grupo, type ListadoCuentas, type TipoCambioSugerido } from "@/lib/api";
import { cajaPorDefecto, hayVariasCajas, nombreCaja, opcionesCaja } from "@/lib/cajas";
import {
  aPayloadAporte,
  filtrosAportesIniciales,
  formAporte,
  opcionesFiduciante,
  queryAportes,
  TODOS,
  validarAporte,
  type FiltrosAportes,
  type FormAporte,
} from "@/lib/aportes";
import { aPayloadAporteGrupo, opcionesGrupo, repartirGrupo, validarAporteGrupo, type ErroresAporteGrupo, type PayloadAporteGrupo } from "@/lib/grupos";

const SIN_ERRORES: ErroresAporteGrupo = {};

/** Quién aporta en el alta: un fiduciante o un grupo de aportantes (Mati, 2026-10-19). */
export type Aporta = "fiduciante" | "grupo";
const OPCIONES_APORTA: { valor: Aporta; etiqueta: string }[] = [
  { valor: "fiduciante", etiqueta: "Un fiduciante" },
  { valor: "grupo", etiqueta: "Un grupo" },
];

type Dialogo = { tipo: "form"; aporte: Aporte | null } | { tipo: "borrar"; aporte: Aporte } | null;

/** Lógica de Aportes (sin JSX). Admin y contador ven cuentas y aportes. Sólo el admin escribe. */
export function useAportesTab() {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}`;
  const esAdmin = membresia.rol === "admin";

  const [filtros, setFiltros] = useState<FiltrosAportes>(filtrosAportesIniciales);
  const aportes = useApiResource<Aporte[]>(`${base}/aportes${queryAportes(filtros)}`);
  const cuentas = useApiResource<ListadoCuentas>(`${base}/cuentas`);
  const fiduciantes = useApiResource<FiduciantePublico[]>(`${base}/fiduciantes`);
  const tcSugerido = useApiResource<TipoCambioSugerido>(esAdmin ? `${base}/tipo-cambio-sugerido` : null);
  const cajas = useApiResource<Caja[]>(`${base}/cajas`); // admin y contador (los únicos que entran acá)
  const grupos = useApiResource<Grupo[]>(esAdmin ? `${base}/grupos` : null);

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormAporte>(() => formAporte());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const [aporta, setAporta] = useState<Aporta>("fiduciante");
  const [grupoId, setGrupoId] = useState("");
  const accionGuardar = useAsyncAction((id: number | null, datos: ReturnType<typeof aPayloadAporte>) =>
    id === null ? api.post<Aporte>(`${base}/aportes`, datos) : api.patch<Aporte>(`${base}/aportes/${id}`, datos),
  );
  const accionGrupo = useAsyncAction((datos: PayloadAporteGrupo) => api.post<AporteGrupo>(`${base}/aportes-grupo`, datos));
  const accionBorrar = useAsyncAction((id: number) => api.delete<void>(`${base}/aportes/${id}`).then(() => true));
  const accionSubir = useAsyncAction((aporteId: number, archivo: File) => {
    const f = new FormData();
    f.append("archivo", archivo);
    return api.upload<Adjunto>(`${base}/aportes/${aporteId}/adjuntos`, f);
  });
  const accionBorrarAdjunto = useAsyncAction((adjuntoId: number) => api.delete<void>(`${base}/adjuntos/${adjuntoId}`).then(() => true));

  const listaGrupos = grupos.data ?? [];
  const nuevo = dialogo?.tipo === "form" && dialogo.aporte === null;
  const delGrupo = nuevo && aporta === "grupo";
  const grupo = listaGrupos.find((g) => String(g.id) === grupoId) ?? null;
  const errores: ErroresAporteGrupo = delGrupo ? validarAporteGrupo(form, grupo) : validarAporte(form);
  const recargar = () => {
    aportes.recargar();
    cuentas.recargar();
  };
  const cerrar = () => setDialogo(null);

  const abrirNuevo = () => {
    setForm(formAporte(undefined, undefined, tcSugerido.data?.tipo_cambio, cajaPorDefecto(cajas.data ?? [])));
    setMostrarErrores(false);
    setAporta("fiduciante");
    setGrupoId("");
    accionGuardar.limpiarError();
    accionGrupo.limpiarError();
    setDialogo({ tipo: "form", aporte: null });
  };
  const abrirEditar = (a: Aporte) => {
    setForm(formAporte(a));
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "form", aporte: a });
  };
  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "form" || Object.keys(errores).length > 0) return;
    const ok = delGrupo ? await accionGrupo.run(aPayloadAporteGrupo(form, grupo?.id ?? null)) : await accionGuardar.run(dialogo.aporte?.id ?? null, aPayloadAporte(form));
    if (ok) {
      cerrar();
      recargar();
    }
  };
  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar") return;
    if (await accionBorrar.run(dialogo.aporte.id)) {
      cerrar();
      recargar();
    }
  };

  const faltan = !aportes.data || !cuentas.data;
  return {
    esAdmin,
    cargando: faltan && (aportes.loading || cuentas.loading),
    errorCarga: faltan ? (aportes.error?.message ?? cuentas.error?.message ?? null) : null,
    /** Ya cargado, un pedido que falla (un filtro, la recarga después de guardar) deja `data` viejo: se avisa en lugar de la lista. */
    errorRecarga: faltan ? null : (aportes.error?.message ?? cuentas.error?.message ?? null),
    reintentar: recargar,
    cuentas: cuentas.data ?? null,
    aportes: aportes.data ?? [],
    filtros,
    cambiarFiltro: <K extends keyof FiltrosAportes>(campo: K, valor: FiltrosAportes[K]) => setFiltros((f) => ({ ...f, [campo]: valor })),
    limpiarFiltros: () => setFiltros(filtrosAportesIniciales()),
    hayFiltros: queryAportes(filtros) !== "",
    opcionesFiltroFiduciante: [{ valor: TODOS, etiqueta: "Todos los fiduciantes" }, ...opcionesFiduciante(fiduciantes.data ?? [])],
    opcionesFiduciante: opcionesFiduciante(fiduciantes.data ?? []),
    dialogo,
    cerrar,
    abrirNuevo,
    abrirEditar,
    abrirBorrar: (a: Aporte) => {
      accionBorrar.limpiarError();
      setDialogo({ tipo: "borrar", aporte: a });
    },
    form,
    cambiar: <K extends keyof FormAporte>(campo: K, valor: FormAporte[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    // El fiduciante se busca escribiendo (Combobox) y el elegido queda como Badge (Mati, 2026-10-19).
    nombreFiducianteForm: (fiduciantes.data ?? []).find((f) => String(f.id) === form.fiduciante_id)?.nombre ?? null,
    elegirFiduciante: (id: string) => setForm((f) => ({ ...f, fiduciante_id: id })),
    quitarFiduciante: () => setForm((f) => ({ ...f, fiduciante_id: "" })),
    // En la lista, a qué caja entró cada aporte (Mati, 2026-10-25); con una sola caja (contando archivadas) no hace falta.
    cajaDe: (cajas.data ?? []).length > 1 ? (a: Aporte) => nombreCaja(cajas.data ?? [], a.caja_id) : null,
    // Dónde entró la plata (Mati, 2026-10-19); con una sola caja activa no se pregunta: va a la principal.
    opcionesCaja: esAdmin && hayVariasCajas(cajas.data ?? []) ? opcionesCaja(cajas.data ?? [], dialogo?.tipo === "form" ? (dialogo.aporte?.caja_id ?? null) : null) : null,
    // Con grupos, el alta pregunta quién aporta; las partes de un aporte del grupo no se editan acá (se corrigen en Grupos).
    opcionesAporta: nuevo && listaGrupos.length > 0 ? OPCIONES_APORTA : null,
    aporta: delGrupo ? "grupo" : "fiduciante",
    cambiarAporta: (v: string) => setAporta(v === "grupo" ? "grupo" : "fiduciante"),
    opcionesGrupo: opcionesGrupo(listaGrupos),
    grupoId,
    elegirGrupo: setGrupoId,
    vistaPreviaGrupo: delGrupo && grupo ? repartirGrupo(form.monto, grupo) : null,
    errores: mostrarErrores ? errores : SIN_ERRORES,
    guardar,
    guardando: accionGuardar.loading || accionGrupo.loading,
    errorGuardar: delGrupo ? accionGrupo.error : accionGuardar.error,
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
    rutaDescarga: (a: Adjunto) => `${base}/adjuntos/${a.id}/descarga`,
    subirA: (aporte: Aporte) => async (archivo: File) => {
      accionBorrarAdjunto.limpiarError();
      if (await accionSubir.run(aporte.id, archivo)) recargar();
    },
    borrarAdjunto: async (a: Adjunto) => {
      accionSubir.limpiarError();
      if (await accionBorrarAdjunto.run(a.id)) recargar();
    },
    subiendo: accionSubir.loading,
    errorAdjuntos: accionSubir.error ?? accionBorrarAdjunto.error,
  };
}
